import { CommonUtility } from '@utility/common';
import { APIPath } from '../constants/api';
import { BaseService } from './base';
import { CRUDService } from './crud';
import { SyncStatusList } from '../constants/common';
import type {
  IMarketplaceMeta,
  MarketplaceListItem,
  IMarketplaceDetails,
  IConnectedMarketplace,
  IContinent,
  ISyncKey,
} from 'types/hook/marketplace';
import { marketplaceWordmarkImages } from '@utility/constants/marketplace';

class MarketPlace extends CRUDService {
  constructor() {
    super(APIPath.marketplace);
  }

  getOAuthConfig = marketplaceId =>
    BaseService.get(`${APIPath.marketplaceOAuthConfig}/${marketplaceId}`);

  getUnAssignedItems = marketplaceId =>
    BaseService.get(`${APIPath.unassignedMarketplaceItem}/${marketplaceId}`);

  resolveUnAssignedItems = (marketplaceId, payload) =>
    BaseService.put(
      `${APIPath.unassignedMarketplaceItem}/${marketplaceId}`,
      payload,
    );

  saveOAuthConfig = (marketplaceId, data) =>
    BaseService.put(`${APIPath.marketplaceOAuthConfig}/${marketplaceId}`, data);

  shippingSettings = data => BaseService.put(APIPath.marketplaceShipping, data);

  getShippingSettings = region =>
    BaseService.get(`${APIPath.marketplaceShipping}/${region}`);

  credentials = data => BaseService.put(APIPath.marketplaceCredentials, data);

  getOrderList() {
    return BaseService.get(APIPath.importOrderList);
  }

  getMarketplaceRegions() {
    return BaseService.get(APIPath.marketplaceRegion);
  }

  toggleMarketplaceBridge(data) {
    return BaseService.put(APIPath.marketplaceBridge, data);
  }

  getMarketplaceSync(params) {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.marketplaceSync}?${query}`);
  }

  updateSeed(data) {
    return BaseService.put(APIPath.marketplaceBridge, data);
  }

  updateOrderDelay(data) {
    return BaseService.put(APIPath.marketplaceOrderDelay, data);
  }

  connectTemu(data) {
    return BaseService.post(APIPath.marketplaceTemu, data);
  }

  connectNetsuite(data) {
    return BaseService.put(APIPath.netsuiteIntegrate, data);
  }
}

const MarketPlaceService = new MarketPlace();
Object.freeze(MarketPlaceService);
export default MarketPlaceService;

export function getMarketplaceList(
  marketplaces: IMarketplaceMeta[],
  includeManual: boolean,
  includeUnconnected: boolean,
): MarketplaceListItem[] {
  const data: MarketplaceListItem[] = [];
  const hasManual = marketplaces.filter(x => x.type === 'manual');
  const markets = includeUnconnected
    ? marketplaces
    : marketplaces.filter(x => !!x.marketplace_connected);

  markets.forEach(market => {
    if (!data.find(x => x.value === market.type)) {
      data.push({
        key: market.type,
        text: market.type,
        label: market.type,
        value: market.type,
        image: {
          src:
            market.type === 'Temu'
              ? marketplaceWordmarkImages[market.type]
              : market.logo_uri || '',
        },
        item: market,
      });
    }
  });

  if (hasManual.length && includeManual) {
    data.push({
      key: 'manual',
      text: 'Manual',
      label: 'manual',
      value: 'manual',
      image: { src: '/favicon.png' },
      item: {},
    });
  }

  return data;
}

const uniqueBy = <T, K>(arr: readonly T[], key: (v: T) => K): T[] => {
  const map = new Map<K, T>();
  for (const item of arr) map.set(key(item), item);
  return Array.from(map.values());
};

type UISyncTemplate = Readonly<{ key: ISyncKey; text: string }>;
type ProcessMarketplaceDataResultT = {
  connectedMarketPlaces: IConnectedMarketplace[];
  tobeAddMarketPlaces: IMarketplaceMeta[];
  continents: string[];
  seedMarketplace: IMarketplaceMeta | null;
  uniqueConnectedMP: IConnectedMarketplace[];
  marketplaces: IMarketplaceMeta[];
};
export function processMarketplaceData(
  marketplaceMetaData: IMarketplaceMeta[],
): ProcessMarketplaceDataResultT {
  const uniqueAreas: string[] = [];
  const seedM = marketplaceMetaData.find(x => x.is_seed) ?? null;
  const rows: IConnectedMarketplace[] = [];
  const SYNC_TEMPLATES = SyncStatusList as ReadonlyArray<UISyncTemplate>;

  marketplaceMetaData
    .filter(
      x =>
        Array.isArray(x.marketplaces_details) &&
        x.marketplaces_details.some(d => d.marketplace_connected) &&
        x.status !== 3 &&
        x.status !== 0,
    )
    .forEach(marketplace => {
      if (!marketplace.continent) return;
      const continent = marketplace.continent as IContinent;

      (marketplace.marketplaces_details as IMarketplaceDetails[]).forEach(
        detail => {
          const merged: IConnectedMarketplace = {
            ...(marketplace as IMarketplaceMeta),
            ...(detail as IMarketplaceDetails),
            is_bridged: detail.is_seed ? true : undefined,
            syncData: [],
            status: marketplace.status,
            continent,
          };

          const connection = merged.connection_status ?? {};

          merged.syncData = SYNC_TEMPLATES.map(sync => {
            const val = connection[sync.key];
            const status = typeof val === 'number' ? val : -1;
            return { key: sync.key, text: sync.text, status };
          }).filter(s => s.status !== -1);

          if (!uniqueAreas.includes(merged.continent.name)) {
            uniqueAreas.push(merged.continent.name);
          }

          rows.push(merged);
        },
      );
    });

  const integrationMarketplaces = marketplaceMetaData.filter(
    x => !x.marketplace_connected && x.status !== 3 && x.status !== 0,
  );

  return {
    connectedMarketPlaces: rows,
    tobeAddMarketPlaces: integrationMarketplaces,
    continents: uniqueAreas,
    seedMarketplace: seedM,
    uniqueConnectedMP: uniqueBy(rows, i => i.type),
    marketplaces: uniqueBy(integrationMarketplaces, i => i.type),
  };
}
